/**
 * 🔒 The stored-name convention (decision 2421): every stored version of a file
 * is indexed under a name the application writes, never the name the person
 * typed, which the version keeps beside it as its original file name.
 *
 *   <record kind>-<record number or name>-<type slug>-<yyyy-mm-dd>-v<n>.<ext>
 *
 * `company-northgate-systems-w9-2026-10-07-v2.pdf` is a vendor's second W-9. A
 * file tagged to no record reads `inbox` for its kind and carries no record part.
 * The display name a person sees stays the document's own and never follows it.
 */

/** The word each record kind is named by; a kind this list does not hold is its own name in lower case with hyphens. */
export const STORED_NAME_RECORD_KINDS: Readonly<Record<string, string>> = {
  CompanyRecord: 'company',
  PersonRecord: 'person',
  SiteRecord: 'site',
  LocationRecord: 'location',
  AssetNode: 'asset',
  EmploymentRecord: 'employment',
  DisciplinaryRecord: 'disciplinary',
  UnemploymentClaim: 'unemployment-claim',
  PTORequest: 'pto-request',
  RFQ: 'rfq',
  BOM: 'bom',
};

/** The longest a record or type part may run, so a long name never makes an unwieldy stored name. */
export const STORED_NAME_PART_MAX = 40;

/** A name as a slug: lower case, letters and digits only, words joined by one hyphen, at most the part limit. */
export function nameSlug(text: string): string {
  const slug = text
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.slice(0, STORED_NAME_PART_MAX).replace(/-+$/, '');
}

/** The word a record kind is named by in a stored name. */
export function storedNameRecordKind(recordType: string | null): string {
  if (!recordType) return 'inbox';
  return STORED_NAME_RECORD_KINDS[recordType] ?? nameSlug(recordType.replace(/([a-z0-9])([A-Z])/g, '$1-$2'));
}

/** The original file name's last extension, lower-cased with its dot, or nothing where it has none. */
export function storedNameExtension(filename: string): string {
  const match = /\.([A-Za-z0-9]{1,10})$/.exec(filename.trim());
  return match ? `.${match[1]!.toLowerCase()}` : '';
}

export interface StoredNameFacts {
  /** The tagged record's kind, or null for a file tagged to no record. */
  recordType: string | null;
  /** The record's number where it carries one, else its name; null when it cannot be read. */
  recordLabel: string | null;
  /** The system-assigned type's own slug where it has one, else the type's name. */
  typeSlugOrName: string;
  /** The day the version was uploaded, in the organization's zone. */
  uploadedOn: string;
  versionNumber: number;
  /** What the person's file was called, whose extension the stored name keeps. */
  originalFilename: string;
}

/** The stored name of one version under the convention (decision 2421). */
export function storedFileName(facts: StoredNameFacts): string {
  const kind = storedNameRecordKind(facts.recordType);
  const record = facts.recordType && facts.recordLabel ? nameSlug(facts.recordLabel) : '';
  const type = nameSlug(facts.typeSlugOrName) || 'file';
  const parts = [kind, ...(record ? [record] : []), type, facts.uploadedOn, `v${facts.versionNumber}`];
  return `${parts.join('-')}${storedNameExtension(facts.originalFilename)}`;
}

/**
 * 🔒 A stored name that another version already holds takes the next number
 * before its extension (decision 2440): `…-v1.pdf` becomes `…-v1-2.pdf`, then
 * `…-v1-3.pdf`. A number of 1 or less leaves the name as it is.
 */
export function storedNameWithCollision(name: string, collision: number): string {
  if (collision <= 1) return name;
  const extension = storedNameExtension(name);
  return `${extension ? name.slice(0, -extension.length) : name}-${collision}${extension}`;
}

/**
 * 🔒 The same-name question (decision 2421): a generic upload whose name is
 * already a document's on the record asks whether it is that document's new
 * version or a separate file, and suggests the answer. A matching type suggests
 * the new version; a different type suggests a separate file.
 */
export type SameNameAnswer = 'version' | 'separate';

export function sameNameSuggestion(pickedFileTypeId: string, standingFileTypeId: string): SameNameAnswer {
  return pickedFileTypeId === standingFileTypeId ? 'version' : 'separate';
}

/** Whether two file names are the same document's name: compared without case or surrounding spaces. */
export function sameFileName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}
